/**
 * Liturgical Tones Utility for Malankara Orthodox Church
 * Based on the Beth Gazo eight-tone system
 */

import { getFeastDay, FIXED_FEAST_DAYS, MOVABLE_FEAST_PATTERNS } from './feastDayTones';

// SoundCloud links for the 8 liturgical tones
export const TONE_LINKS = {
  1: "https://soundcloud.com/joshmaret/quqlion-tone-1",
  2: "https://soundcloud.com/joshmaret/quqlion-tone-2",
  3: "https://soundcloud.com/joshmaret/quqlion-tone-3",
  4: "https://soundcloud.com/joshmaret/quqlion-tone-4",
  5: "https://soundcloud.com/joshmaret/quqlion-tone-5",
  6: "https://soundcloud.com/joshmaret/quqlion-tone-6",
  7: "https://soundcloud.com/joshmaret/quqlion-tone-7",
  8: "https://soundcloud.com/joshmaret/quqlion-tone-8"
};

// Tone names in Syriac
export const TONE_NAMES = {
  1: "Qadmoyo (First Tone)",
  2: "Trayono (Second Tone)",
  3: "Tlithoyo (Third Tone)",
  4: "Rbi'oyo (Fourth Tone)",
  5: "Hmishoyo (Fifth Tone)",
  6: "Shithoyo (Sixth Tone)",
  7: "Shbi'oyo (Seventh Tone)",
  8: "Tminoyo (Eighth Tone)"
};

/**
 * Find Qudosh Idto (Consecration of the Church) date for a given year
 * Qudosh Idto falls on:
 * - The last Sunday of October (if it's Oct 30 or 31)
 * - OR the first Sunday of November
 *
 * @param {number} year - The year to find Qudosh Idto for
 * @returns {Date} - The date of Qudosh Idto for that year
 */
export function findQudoshIdto(year) {
  // Check October 30 and 31
  for (let day = 31; day >= 30; day--) {
    const date = new Date(Date.UTC(year, 9, day)); // Month 9 = October (0-indexed)
    if (date.getUTCDay() === 0) { // Sunday
      return date;
    }
  }

  // If no Sunday on Oct 30-31, find first Sunday of November
  for (let day = 1; day <= 7; day++) {
    const date = new Date(Date.UTC(year, 10, day)); // Month 10 = November (0-indexed)
    if (date.getUTCDay() === 0) { // Sunday
      return date;
    }
  }

  // Fallback (should never reach here)
  return new Date(Date.UTC(year, 10, 1));
}

/**
 * Find the nearest reset point before a given date
 * Reset points start a new 8-week tone cycle
 *
 * @param {Date} targetDate - The date to find reset point for
 * @param {Array} lectionaryEvents - All lectionary events for the year (optional, for movable feasts)
 * @returns {Object} - { date: Date, tone: number } of the reset point
 */
function findNearestResetPoint(targetDate, lectionaryEvents = []) {
  const year = targetDate.getFullYear();
  const candidates = [];

  // Add Qudosh Idto (always a reset point)
  const qudoshIdtoCurrentYear = findQudoshIdto(year);
  const qudoshIdtoPreviousYear = findQudoshIdto(year - 1);

  if (targetDate >= qudoshIdtoCurrentYear) {
    candidates.push({ date: qudoshIdtoCurrentYear, tone: 1, name: "Qudosh Idto" });
  } else {
    candidates.push({ date: qudoshIdtoPreviousYear, tone: 1, name: "Qudosh Idto" });
  }

  // Add fixed feast reset points (Christmas, Holy Cross)
  Object.entries(FIXED_FEAST_DAYS).forEach(([monthDay, feast]) => {
    if (feast.isResetPoint) {
      const [month, day] = monthDay.split('-').map(Number);
      const feastDate = new Date(Date.UTC(year, month - 1, day));

      // Check both current and previous year
      if (feastDate <= targetDate) {
        candidates.push({ date: feastDate, tone: feast.tone, name: feast.name });
      }

      const feastDatePrevYear = new Date(Date.UTC(year - 1, month - 1, day));
      if (feastDatePrevYear <= targetDate) {
        candidates.push({ date: feastDatePrevYear, tone: feast.tone, name: feast.name });
      }
    }
  });

  // Add movable feast reset points from lectionary events
  if (lectionaryEvents && lectionaryEvents.length > 0) {
    lectionaryEvents.forEach(event => {
      const movableFeast = Object.values(MOVABLE_FEAST_PATTERNS).find(feast =>
        feast.isResetPoint && feast.keywords.some(keyword =>
          event.summary.toLowerCase().includes(keyword.toLowerCase())
        )
      );

      if (movableFeast) {
        const [eventYear, eventMonth, eventDay] = event.date.split('-').map(Number);
        const eventDate = new Date(Date.UTC(eventYear, eventMonth - 1, eventDay));

        if (eventDate <= targetDate) {
          candidates.push({
            date: eventDate,
            tone: movableFeast.tone,
            name: movableFeast.name
          });
        }
      }
    });
  }

  // Find the most recent reset point before target date
  candidates.sort((a, b) => b.date - a.date); // Sort descending
  return candidates[0] || { date: qudoshIdtoPreviousYear, tone: 1, name: "Qudosh Idto" };
}

/**
 * Calculate the liturgical tone for a given date
 * The tone cycles through 1-8 starting from the nearest reset point
 *
 * @param {string|Date} dateInput - Date string in YYYY-MM-DD format or Date object
 * @param {string} eventName - Optional event name from calendar for feast day matching
 * @param {Array} lectionaryEvents - Optional array of all lectionary events for finding movable reset points
 * @returns {number|null} - Tone number (1-8) if it's a Sunday, null otherwise
 */
export function calculateLiturgicalTone(dateInput, eventName = null, lectionaryEvents = []) {
  let targetDate;

  // Parse date input
  if (typeof dateInput === 'string') {
    // Parse YYYY-MM-DD format using UTC to avoid timezone issues
    const [year, month, day] = dateInput.split('-').map(Number);
    targetDate = new Date(Date.UTC(year, month - 1, day));
  } else {
    targetDate = new Date(dateInput);
  }

  // PRIORITY 1: Check for feast day tone override (both fixed and movable)
  const feastDay = getFeastDay(targetDate, eventName);
  if (feastDay && feastDay.tone !== null) {
    return feastDay.tone;
  }

  // Only calculate tone for Sundays (feast days already handled above)
  if (targetDate.getUTCDay() !== 0) {
    return null;
  }

  // PRIORITY 2: Find the nearest reset point before this date
  const resetPoint = findNearestResetPoint(targetDate, lectionaryEvents);

  // If the target date IS the reset point (it's a Sunday feast day reset),
  // it already got its tone from the feast day check above
  // This section only runs for regular Sundays

  // Calculate number of weeks from the reset point
  const millisecondsDiff = targetDate - resetPoint.date;
  const daysDiff = Math.floor(millisecondsDiff / (1000 * 60 * 60 * 24));
  const weeksDiff = Math.ceil(daysDiff / 7); // Use ceil to count partial weeks as 1 week

  // Calculate tone starting from the reset point's tone
  // weeksDiff=1 means first Sunday after reset, so we advance by 1 tone
  const tone = ((resetPoint.tone - 1 + weeksDiff) % 8) + 1;

  return tone;
}

/**
 * Get tone information for a given date
 *
 * @param {string|Date} dateInput - Date string in YYYY-MM-DD format or Date object
 * @param {string} eventName - Optional event name from calendar for feast day matching
 * @param {Array} lectionaryEvents - Optional array of all lectionary events for finding movable reset points
 * @returns {Object|null} - Object with tone number, name, link, and feast info, or null if not applicable
 */
export function getToneInfo(dateInput, eventName = null, lectionaryEvents = []) {
  const tone = calculateLiturgicalTone(dateInput, eventName, lectionaryEvents);

  if (tone === null) {
    return null;
  }

  // Check if this is a feast day
  const feastDay = getFeastDay(dateInput, eventName);

  return {
    tone: tone,
    name: TONE_NAMES[tone],
    link: TONE_LINKS[tone],
    isFeastDay: feastDay !== null,
    feastName: feastDay ? feastDay.name : null
  };
}

/**
 * Check if a given date is a Sunday
 *
 * @param {string|Date} dateInput - Date string in YYYY-MM-DD format or Date object
 * @returns {boolean} - True if Sunday, false otherwise
 */
export function isSunday(dateInput) {
  let targetDate;

  if (typeof dateInput === 'string') {
    const [year, month, day] = dateInput.split('-').map(Number);
    targetDate = new Date(Date.UTC(year, month - 1, day));
  } else {
    targetDate = new Date(dateInput);
  }

  return targetDate.getUTCDay() === 0;
}
